import * as React from "react";
import { useEffect } from "react";
import { Chip, ChipProps } from "@mui/material";
import { GridColumns, GridRenderCellParams } from "@mui/x-data-grid";
import { getBatches, deleteBatch } from "../../API/Batches";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import { ERROR_TOAST, SUCCESS_TOAST } from "../../helpers/toasters";
import CreateBatch from "../../components/createNewBatch/createBatch";
import { CourseData, CourseinitialUpdatingRow } from "../courses/CoursesList";
import MainTable from "../../components/Datagrid/dataGridTable";
import { useSelector } from "react-redux";

function getChipProps(params: GridRenderCellParams): ChipProps {
  const days = params.value || [];

  const DAYS_MAPPING = {
    1: "Mon",
    2: "Tue",
    3: "Wed",
    4: "Thu",
    5: "Fri",
    6: "Sat",
    7: "Sun"
  };

  const formattedDays = days
    .map(day => DAYS_MAPPING[day] ?? null)
    .filter(day => day !== null)
    .join(", ");

  return {
    label: formattedDays || "N/A",
    style: {
      background: "#7A9BF9",
      width: 500,
      height: "30px",
      color: "#fff",
      fontFamily: "Poppins",
      fontStyle: "normal",
      fontWeight: 500,
      fontSize: "11px",
      lineHeight: "16px",
      borderColor: "#7A9BF9",
    },
  };
}

function getChipProps2(params: GridRenderCellParams): ChipProps {
  return {
    label: params.value,
    style: {
      background: "#7A9BF9",
      width: 500,
      height: "30px",
      color: "#fff",
      fontFamily: "Poppins",
      fontStyle: "normal",
      fontWeight: 500,
      fontSize: "11px",
      lineHeight: "16px",
      borderColor: "#7A9BF9",
    },
  };
}

export type CourseCategoryData = {
  _id: string;
};

type data = {
  days_type: string;
  type: string;
  active: boolean;
  course: CourseData[];
  students: string[];
  timeslot: string;
  coach: string;
  batch_id: String;
  repeats: number[];
  _id: string;
};

export const initialCourseCategoty = {
  _id: "",
};

const initialUpdatingRow = {
  days_type: "",
  type: "",
  active: true,
  course: [
    {
      ...CourseinitialUpdatingRow,
    },
  ],
  course_category: [
    {
      ...initialCourseCategoty,
    },
  ],
  students: [],
  timeslot: "",
  coach: "",
  batch_id: 0,
  repeats: [],
  _id: "",
};

export default function BatchList(props) {
  const [pageSize, setPageSize] = React.useState<number>(5);
  const [rows, setRows] = React.useState<String[]>([]);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [updateCourse, setUpdateCourse] = React.useState(false);
  const [updatingRow, setUpdatingRow] = React.useState<any>(initialUpdatingRow);
  const [searchRows, setSearchRows] = React.useState<String[]>([]);
  const user = useSelector((state: any) => state.auth);

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    if (!props.search) {
      setSearchRows(rows);
    } else {
      const filteredRows = rows.filter((row: any) =>
        row.name.toLowerCase().includes(props.search.toLowerCase()) ||
        row.batch_id.toString().includes(props.search) ||
        (row.course[0]?.name || "").toLowerCase().includes(props.search.toLowerCase()) ||
        row.coach?.some((coach: any) => (coach.name || "").toLowerCase().includes(props.search.toLowerCase()))
      );
      setSearchRows(filteredRows);
    }
  }, [props.search, rows]);

  const handleCellClick = (
    e: React.MouseEvent,
    value: GridRenderCellParams
  ) => {
    const { row } = value;
    setUpdateCourse(true);
    setUpdatingRow(row);
  };

  const getData = async () => {
    const data = await getBatches();
    if (data) {
      setRows(data);
      setLoading(false);
    } else {
      return ERROR_TOAST("Server error, Please refresh the page !");
    }
  };

  const handleDelete = async (row: any) => {
    if (window.confirm("Are you sure you want to delete this batch?")) {
      try {
        await deleteBatch(row._id);
        
        SUCCESS_TOAST("Batch deleted successfully! ✅");
        getData();
      } catch (error) {
        console.error("Failed to delete batch:", error);
        ERROR_TOAST("Failed to delete batch ❌");
      }
    }
  };

  // Base columns visible to all users
  const baseColumns: GridColumns = [
    {
      field: "_id",
      hide: true,
    },
    {
      field: "batch_id",
      headerName: "Batch ID",
      width: 80,
    },
    {
      field: "name",
      headerName: "Batch Name",
      width: 150,
    },
    {
      field: "course",
      headerName: "Course Name",
      valueFormatter: (params) => params.value?.[0]?.name || "N/A",
      width: 210,
      editable: true,
    },
    {
      field: "coach",
      headerName: "Coach / Mentor",
      valueFormatter: (params) => params.value?.map(coach => coach.name).join(", ") || "N/A",
      width: 250,
      editable: false,
    },
    {
      field: "days",
      headerName: "Days",
      renderCell: (params: any) => {
        return (
          <Chip variant="outlined" size="small" {...getChipProps(params)} />
        );
      },
      width: 200,
      editable: false,
    },
    {
      field: "timeslot",
      headerName: "Timeslot",
      renderCell: (params: any) => {
        return (
          <Chip variant="outlined" size="small" {...getChipProps2(params)} />
        );
      },
      width: 180,
      editable: false,
    },
    {
      field: "edit",
      headerName: "Edit",
      width: 50,
      renderCell: (cellValues: any) => {
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <EditRoundedIcon
              data-testid="EditIcon"
              style={{ color: "#8E8EA1", cursor: "pointer" }}
              onClick={(event) => {
                handleCellClick(event, cellValues);
              }}
            />
          </div>
        );
      },
    },
  ];

  // Action columns visible only to superusers
  const actionColumns: GridColumns = [

    {
      field: "delete",
      headerName: "Delete",
      width: 100,
      renderCell: (cellValues: any) => {
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <DeleteRoundedIcon
              data-testid="DeleteIcon"
              style={{ color: "#8E8EA1", cursor: "pointer" }}
              onClick={() => handleDelete(cellValues.row)}
            />
          </div>
        );
      },
    },
  ];

  // Combine columns based on user.isSuper
  const columns = user.isSuper ? [...baseColumns, ...actionColumns] : baseColumns;

  const handelClose = () => {
    setUpdateCourse(false);
    getData();
  };

  return (
    <>
      <MainTable rows={searchRows} columns={columns} loading={loading} />
      {updateCourse && (
        <CreateBatch handelClose={handelClose} data={updatingRow} />
      )}
    </>
  );
}